/* eslint-disable react/prop-types */
/**
 * /projects/workbench/:did/reports/:rid/pdf — Iter 255.
 *
 * Why this page exists
 * ────────────────────
 * On iOS Safari running as a PWA (Add-to-Home-screen / standalone mode),
 * navigating to a backend PDF URL via `window.location.href` shows a
 * white-then-black screen and never renders the document. The PWA's
 * `display: standalone` removes Safari's chrome — including the native
 * PDF viewer toolbar — and iOS refuses to render an `application/pdf`
 * response inside the PWA web view as a top-level navigation.
 *
 * The fix is to keep the user INSIDE the PWA and render the PDF in our
 * own controlled chrome:
 *   1. Fetch the PDF as an authenticated blob through our axios client.
 *   2. Display it via `<iframe src={blob:...}>` — iOS Safari WebView
 *      DOES render same-origin blob URLs in iframes (since iOS 15).
 *   3. Provide our own Back / Share / Download buttons at the top.
 *      • Back: react-router `navigate(-1)`.
 *      • Share: `navigator.share({ files: [pdfFile] })` — opens the
 *        native iOS share sheet (WhatsApp, Mail, AirDrop, Files…).
 *      • Download: same `navigator.share` flow on iOS (Files.app is one
 *        tap from the share sheet). On Android/desktop, falls back to
 *        a normal `<a download>` click.
 *
 * The desktop flow is unchanged — desktop still uses the inline iframe
 * preview inside ProjectWorkbenchDetail itself. This route is the
 * mobile-PWA entry point: Export PDF on mobile now navigates here.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Share2, Download, Loader2, AlertTriangle, Home, RotateCw } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "../components/ui/sonner";

export default function WorkbenchPdfViewer() {
  const { did, rid } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [filename, setFilename] = useState("workbench-report.pdf");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        // Get the metadata first so we can name the file properly.
        // Falls back silently if the meta endpoint is unreachable — the
        // PDF itself is the priority.
        try {
          const { data } = await api.get(`/workbench/drafts/${did}/reports/${rid}`);
          if (!cancelled && data) {
            const base = (data.name || data.headline || "workbench-report")
              .replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "");
            setFilename((base || "workbench-report") + ".pdf");
          }
        } catch { /* non-fatal */ }

        const res = await api.get(
          `/workbench/drafts/${did}/reports/${rid}/pdf`,
          {
            responseType: "blob",
            // [Iter 258] explicit 60s timeout — PDF generation is fast
            // after caching (<50ms cache hit) but the FIRST render can
            // take 8-10s. Default axios timeout was undefined which
            // inherited the browser fetch default and on iOS Safari
            // randomly killed the connection mid-stream → user saw
            // "Request failed with status code 520".
            timeout: 60_000,
          },
        );
        if (cancelled) return;
        const blob = new Blob([res.data], { type: "application/pdf" });
        createdUrl = window.URL.createObjectURL(blob);
        setPdfBlob(blob);
        setBlobUrl(createdUrl);
      } catch (e) {
        if (!cancelled) {
          // [Iter 258] Friendlier error message — was showing raw axios
          // text "Request failed with status code 520" which is what
          // Dirk saw and rightly called unacceptable. Now we map the
          // common failure modes (520, timeout, 5xx, network) to plain
          // English the user can actually act on.
          const status = e?.response?.status;
          const code = e?.code;
          let friendly;
          if (status === 520 || status === 502 || status === 503 || status === 504) {
            friendly = "The server is taking too long to generate this PDF. Try again in a moment.";
          } else if (code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
            friendly = "The PDF took too long to load. Tap Retry to try again.";
          } else if (status === 403) {
            friendly = "You're not signed in to this tenant. Tap Home and sign in again.";
          } else if (status === 404) {
            friendly = "This report no longer exists. Tap Home to go back to your work.";
          } else if (status === 400) {
            friendly = formatErr(e?.response?.data?.detail, "Report is still being generated. Try again shortly.");
          } else if (!status && (code === "ERR_NETWORK" || /network/i.test(e?.message || ""))) {
            friendly = "No internet connection. Reconnect and tap Retry.";
          } else {
            friendly = formatErr(e?.response?.data?.detail || e?.message, "Couldn't load the PDF. Tap Retry or Home.");
          }
          setError(friendly);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) {
        try { window.URL.revokeObjectURL(createdUrl); } catch { /* ignore */ }
      }
    };
  }, [did, rid, attempt]);

  const onBack = useCallback(() => {
    // Prefer history back so the user lands on the workbench they came
    // from. Falls through to the workbench list if there's no history
    // (deep-link / fresh tab case).
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`/projects/workbench/${did}`);
    }
  }, [did, navigate]);

  const onShare = useCallback(async () => {
    if (!pdfBlob) return;
    const file = new File([pdfBlob], filename, { type: "application/pdf" });
    // Prefer file share (iOS / Android / new Chrome on macOS).
    if (
      typeof navigator !== "undefined"
      && typeof navigator.canShare === "function"
      && typeof navigator.share === "function"
      && navigator.canShare({ files: [file] })
    ) {
      try {
        await navigator.share({
          files: [file],
          title: filename,
          text: "Engineering report",
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
        // fall through to download fallback
      }
    }
    // No file-share support — fall back to triggering a download so the
    // user can manually attach the file from their Files / Downloads
    // app.
    triggerDownload();
    toast.message("Sharing not supported on this browser — saved to device instead.", {
      description: "Attach the file from Files / Downloads.",
    });
  }, [pdfBlob, filename]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerDownload = useCallback(() => {
    if (!pdfBlob || !blobUrl) return;
    // On iOS, `<a download>` does NOT work for blob URLs — the link
    // opens inline instead of triggering a save. The reliable path is
    // navigator.share with the Files.app target. Detect iOS first.
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
    const file = new File([pdfBlob], filename, { type: "application/pdf" });
    if (
      isIOS
      && typeof navigator !== "undefined"
      && typeof navigator.canShare === "function"
      && typeof navigator.share === "function"
      && navigator.canShare({ files: [file] })
    ) {
      // The iOS share sheet has "Save to Files" right there — same UX
      // as a download. Fire-and-forget; promise rejection on abort is
      // expected and silently ignored.
      navigator.share({
        files: [file],
        title: filename,
        text: "Engineering report from HidroVerse",
      }).catch((err) => {
        if (err?.name !== "AbortError") {
          toast.error("Couldn't open save dialog. Try the Share button.");
        }
      });
      return;
    }
    // Android / Desktop — anchor click works as expected.
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Saved to device.");
  }, [pdfBlob, blobUrl, filename]);

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-[#0a0e1a] text-white"
      data-testid="workbench-pdf-viewer"
    >
      {/* Top toolbar — sticky, safe-area aware for iPhone notch. */}
      <div
        className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10 bg-[#0f1525]/90 backdrop-blur"
        style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-white/90 hover:bg-white/10 active:bg-white/20 transition-colors"
            data-testid="workbench-pdf-viewer-back"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center justify-center rounded-md px-2 py-1.5 text-white/90 hover:bg-white/10 active:bg-white/20 transition-colors"
            data-testid="workbench-pdf-viewer-home"
            title="Home"
            aria-label="Home"
          >
            <Home className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 min-w-0 text-center text-xs text-white/60 truncate px-2" title={filename}>
          {filename}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onShare}
            disabled={!pdfBlob}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-white/90 hover:bg-white/10 active:bg-white/20 transition-colors disabled:opacity-40"
            data-testid="workbench-pdf-viewer-share"
          >
            <Share2 className="w-4 h-4" />
            <span className="hidden sm:inline">Share</span>
          </button>
          <button
            type="button"
            onClick={triggerDownload}
            disabled={!pdfBlob}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-white/90 hover:bg-white/10 active:bg-white/20 transition-colors disabled:opacity-40"
            data-testid="workbench-pdf-viewer-download"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Download</span>
          </button>
        </div>
      </div>

      {/* PDF body — fills the rest of the viewport. */}
      <div className="flex-1 min-h-0 bg-[#0a0e1a] relative">
        {loading && (
          <div
            className="absolute inset-0 flex items-center justify-center text-white/70"
            data-testid="workbench-pdf-viewer-loading"
          >
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Generating PDF…
          </div>
        )}
        {error && !loading && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center"
            data-testid="workbench-pdf-viewer-error"
          >
            <AlertTriangle className="w-10 h-10 text-rose-400 mb-3" />
            <div className="text-sm text-rose-200 max-w-sm mb-1" data-testid="workbench-pdf-viewer-error-message">{error}</div>
            <div className="text-xs text-white/40 mb-5">{filename}</div>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  setAttempt((n) => n + 1);
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-cyan-500/20 border border-cyan-400/30 px-4 py-2 text-sm text-cyan-100 hover:bg-cyan-500/30 active:bg-cyan-500/40"
                data-testid="workbench-pdf-viewer-retry"
              >
                <RotateCw className="w-4 h-4" />
                Retry
              </button>
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-4 py-2 text-sm text-white hover:bg-white/20"
                data-testid="workbench-pdf-viewer-error-back"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-4 py-2 text-sm text-white hover:bg-white/20"
                data-testid="workbench-pdf-viewer-error-home"
              >
                <Home className="w-4 h-4" />
                Home
              </button>
            </div>
          </div>
        )}
        {blobUrl && !error && (
          <iframe
            title="Workbench report PDF"
            src={blobUrl}
            className="w-full h-full border-0"
            data-testid="workbench-pdf-viewer-iframe"
          />
        )}
      </div>
    </div>
  );
}
